(() => {
    const DESIGN_WIDTH = 1920;
    const DESIGN_HEIGHT = 1080;
    const SCROLL_PAUSE = 1000;
    const MIN_SCROLL_DURATION = 1800;
    const SCROLL_SPEED = 36;

    let rankingScrollController = null;
    let resizeTimer = null;

    const getScaleRatio = () => {
        const widthRatio = window.innerWidth / DESIGN_WIDTH;
        const heightRatio = window.innerHeight / DESIGN_HEIGHT;

        return Math.min(widthRatio, heightRatio);
    };

    const updateScaleRatio = () => {
        const ratio = getScaleRatio();
        document.documentElement.style.setProperty('--scale-ratio', ratio.toFixed(4));

        return ratio;
    };

    const wait = (ms) => new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });

    const waitTransitionEnd = (element, duration) => new Promise((resolve) => {
        let timer = null;

        const cleanup = () => {
            window.clearTimeout(timer);
            element.removeEventListener('transitionend', onEnd);
            resolve();
        };

        const onEnd = (event) => {
            if (event.target === element) cleanup();
        };

        element.addEventListener('transitionend', onEnd);
        timer = window.setTimeout(cleanup, duration + 100);
    });

    const stopRankingScroll = () => {
        if (!rankingScrollController) return;

        rankingScrollController.abort();
        rankingScrollController = null;
    };

    const resetRankingBody = (body) => {
        body.style.transition = 'none';
        body.style.transform = 'translateY(0)';
    };

    const startRankingScroll = () => {
        stopRankingScroll();

        const viewport = document.querySelector('[data-ranking-viewport]');
        const body = document.querySelector('[data-ranking-body]');
        if (!viewport || !body) return;

        resetRankingBody(body);

        const distance = Math.max(0, body.scrollHeight - viewport.clientHeight);
        if (distance <= 1) return;

        rankingScrollController = new AbortController();
        const { signal } = rankingScrollController;
        const speed = SCROLL_SPEED * getScaleRatio();
        const duration = Math.max(MIN_SCROLL_DURATION, (distance / speed) * 1000);

        const run = async () => {
            while (!signal.aborted) {
                await wait(SCROLL_PAUSE);
                if (signal.aborted) break;

                body.style.transition = `transform ${duration}ms linear`;
                body.style.transform = `translateY(-${distance}px)`;
                await waitTransitionEnd(body, duration);
                if (signal.aborted) break;

                await wait(SCROLL_PAUSE);
                if (signal.aborted) break;

                body.style.transition = `transform ${duration}ms linear`;
                body.style.transform = 'translateY(0)';
                await waitTransitionEnd(body, duration);
            }
        };

        run();
    };

    const fetchExcelRows = async (url) => {
        if (!url || !window.XLSX) return [];

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load Excel: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const workbook = window.XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = window.XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: ''
        });

        const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
        const dataRows = rows.slice(1).length ? rows.slice(1) : rows;
        const usedColumnIndexes = [];

        dataRows.forEach((row) => {
            row.forEach((cell, index) => {
                if (hasValue(cell) && !usedColumnIndexes.includes(index)) {
                    usedColumnIndexes.push(index);
                }
            });
        });

        return rows
            .map((row) => usedColumnIndexes.map((index) => row[index]))
            .filter((row) => row.some(hasValue));
    };

    const toPercentNumber = (value) => {
        if (typeof value === 'number') return value;

        const numericValue = Number.parseFloat(String(value).replace('%', '').trim());
        return Number.isFinite(numericValue) ? numericValue : 0;
    };

    const formatPercent = (value) => `${toPercentNumber(value)}%`;

    const getRankClass = (index) => {
        switch (index) {
            case 0:
                return 'ranking-table__rank--one';
            case 1:
                return 'ranking-table__rank--two';
            case 2:
                return 'ranking-table__rank--three';
            default:
                return '';
        }
    };

    const initData = async () => {
        try {
            const rows = await fetchExcelRows(window.fileData);
            // 按照单量完成进度的排序
            const dataRows = rows.slice(1).sort((prev, next) => toPercentNumber(next[1]) - toPercentNumber(prev[1]));

            window.screenDashboardRows = dataRows;
            window.dispatchEvent(new CustomEvent('screen:data-loaded', {
                detail: { rows: dataRows }
            }));

            if (typeof window.screenDashboardReader === 'function') {
                window.screenDashboardReader(dataRows);
            }
        } catch (error) {
            console.error('Screen dashboard: failed to load Excel data.', error);
        }
    };

    const handleResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            updateScaleRatio();
            startRankingScroll();
        }, 120);
    };

    const renderRankingRows = (rows) => {
        const wrap = document.querySelector('.ranking-table__body');
        if (!wrap) return;

        wrap.innerHTML = '';

        rows.forEach((item, index) => {
            const rankClass = getRankClass(index);
            const rankClassName = ['ranking-table__rank', rankClass].filter(Boolean).join(' ');
            const orderValue = toPercentNumber(item[1]);
            const gmvValue = toPercentNumber(item[2]);
            const timeValue = toPercentNumber(item[3]);
            const dom = document.createElement('div');

            dom.className = 'ranking-table__row';
            dom.innerHTML = `
                <div><span class="${rankClassName}">${index + 1}</span></div>
                <div class="item-name">${item[0] || ''}</div>
                <div class="ranking-table__bar ranking-table__bar--cyan"><span style="width: ${orderValue >= 100 ? 100 : orderValue}%;"></span><b>${formatPercent(item[1])}</b></div>
                <div class="ranking-table__bar ranking-table__bar--purple"><span style="width: ${gmvValue >= 100 ? 100 : gmvValue}%;"></span><b>${formatPercent(item[2])}</b></div>
                <div class="ranking-table__bar ranking-table__bar--gold"><span style="width: ${timeValue >= 100 ? 100 : timeValue}%;"></span><b>${formatPercent(item[3])}</b></div>`;

            wrap.appendChild(dom);
        });

        startRankingScroll();
    };

    const init = async () => {
        window.screenDashboardReader = renderRankingRows;

        updateScaleRatio();
        startRankingScroll();
        window.addEventListener('resize', handleResize);

        await initData();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
